import React from "react"
import * as recharts from "recharts"
import * as screen from "shared/screen"
import * as chartUtil from "activities/components/charts/util"
import * as util from "shared/graphs/util"
import * as common from "story/components/graphs/common"

/* TODO: Add more colors */

const _staggerTicks = (vals) => (screen.getScreenWidth() < 620 ? util.staggerTicks(vals, "small") : util.staggerTicks(vals))

export const DoubleBar = ({
  tooltip,
  legend,
  values = [],
  xAxis = "Years",
  yAxis = "Values",
  yAxisWidth = 60,
  formatter = "default",
  colors = colorsArr,
  disabled = false,
  customTooltipStyles = {},
  customLegendStyles = {},
  customXAxisStyles,
  isStacked,
  type,
  ariaLabel,
  staggerTicks = true,
  barSize
}) => {
  const checkedVals = isLastBalanceZero(values[values.length - 1]) ? removeLastElement(values) : values
  const defaultAriaLabel = `${yAxis} per ${xAxis}`
  if (!ariaLabel) console.warn(`No specific ariaLabel provided, using default one '${defaultAriaLabel}'.`)

  return (
    <util.Chart className={type} ariaLabel={ariaLabel || defaultAriaLabel} type={type}>
      <util.AxisLabel label={yAxis} className="yaxis" type={type} />
      <recharts.ResponsiveContainer width={"100%"} height={"100%"}>
        <recharts.BarChart data={util.toRecharts(checkedVals)} margin={{left:18, right: 20, top: 20}}>
          <recharts.XAxis
            dataKey="name"
            interval={0}
            axisLine={false}
            tickLine={false}
            ticks={staggerTicks ? _staggerTicks(checkedVals) : chartUtil.getXAxisTicks(values.length, values)}
            tick={customXAxisStyles ? customXAxisStyles : util.xAxisTickStyles}
          />
          <recharts.YAxis
            allowDecimals={false}
            type="number"
            width={yAxisWidth}
            domain={[0, (dataMax) => util.getYAxisRange(dataMax)]}
            axisLine={false}
            tickLine={false}
            tickFormatter={util.getFormatter(formatter)}
            tick={util.yAxisTickStyles}
          />
          {!disabled &&
            util.forEachSet(checkedVals, (idx) => (
              <recharts.Bar key={idx} stackId={isStacked ? "a" : null} dataKey={idx} isAnimationActive={false} barSize={barSize ? barSize : checkedVals.length < 12 ? 30 : 12}>
                {checkedVals.map(() => (
                  <recharts.Cell key={idx} fill={colors[idx]} />
                ))}
              </recharts.Bar>
            ))}
          {!disabled && tooltip && (
            <recharts.Tooltip
              {...Object.assign({}, common.tooltipProps(customTooltipStyles), {
                tooltip: reverseTooltip(tooltip)
              })}
            />
          )}
          {legend && <recharts.Legend {...Object.assign({}, common.legendProps(customLegendStyles), {legend})} />}
        </recharts.BarChart>
      </recharts.ResponsiveContainer>
      <util.AxisLabel label={xAxis} className="xaxis" type={type} />
    </util.Chart>
  )
}

const getBar = (dotted, dottedIndex, colors, idx, checkedVals) =>
  dotted && idx === dottedIndex ? (
    <recharts.Bar
      key={idx}
      dataKey={idx}
      xAxisId={idx}
      isAnimationActive={false}
      barSize={checkedVals.length < 12 ? 30 : 12}
      stroke={colors[idx]}
      strokeWidth={"1"}
      strokeDasharray={"5"}
      fill={"#efefef"}
    />
  ) : (
    <recharts.Bar key={idx} dataKey={idx} xAxisId={idx} isAnimationActive={false} barSize={checkedVals.length < 12 ? 30 : 12} fill={colors[idx]} />
  )

export const BarWithOverlay = ({
  tooltip,
  legend,
  values = [],
  xAxis = "Years",
  yAxis = "Values",
  formatter = "default",
  colors = colorsArr,
  disabled = false,
  customTooltipStyles = {},
  customLegendStyles = {},
  customXAxisStyles,
  overlay,
  type,
  ariaLabel,
  staggerTicks = true,
  dotted,
  dottedIndex
}) => {
  const checkedVals = isLastBalanceZero(values[values.length - 1]) ? removeLastElement(values) : values

  return (
    <util.Chart className={type} ariaLabel={ariaLabel} type={type}>
      <util.AxisLabel label={yAxis} className="yaxis" type={type} />
      <recharts.ResponsiveContainer width={"100%"} height={"100%"}>
        <recharts.BarChart data={util.toRecharts(checkedVals)} margin={{right: 20, top: 20}}>
          <recharts.XAxis
            dataKey="name"
            xAxisId={0}
            interval={0}
            axisLine={false}
            tickLine={false}
            ticks={staggerTicks ? _staggerTicks(checkedVals) : chartUtil.getXAxisTicks(values.length, values)}
            tick={customXAxisStyles ? customXAxisStyles : util.xAxisTickStyles}
          />
          {overlay && <recharts.XAxis dataKey="name" xAxisId={1} hide />}
          <recharts.YAxis
            allowDecimals={false}
            type="number"
            domain={[0, (dataMax) => util.getYAxisRange(dataMax)]}
            axisLine={false}
            tickLine={false}
            tickFormatter={util.getFormatter(formatter)}
            tick={util.yAxisTickStyles}
          />
          {!disabled && util.forEachSet(checkedVals, (idx) => getBar(dotted, dottedIndex, colors, idx, checkedVals))}
          {!disabled && tooltip && (
            <recharts.Tooltip
              {...Object.assign({}, common.tooltipProps(customTooltipStyles), {
                tooltip: reverseTooltip(tooltip)
              })}
            />
          )}
          {legend && <recharts.Legend {...Object.assign({}, common.legendProps(customLegendStyles), {legend})} />}
        </recharts.BarChart>
      </recharts.ResponsiveContainer>
      <util.AxisLabel label={xAxis} className="xaxis" type={type} />
    </util.Chart>
  )
}

// customLegendStyles only exists if there is something specific to a single graph

const colorsArr = [
  "#FFBB00",
  "#64D254",
  "#C4A8DF",
  "#FF91B0",
  "#6EC5EF",
  "#005F8B",
  "#FF4444",
  "#00BDDC",
  "#65DFC5",
  "#9E6CC7",
  "#FF881F",
  "#FFE7AF",
  "#00637A",
  "#FFCBCF",
  "#006AF9",
  "#FFA971",
  "#EDFCAE",
  "#A7DD00",
  "#077259",
  "#A8AFDA"
]

const reverseTooltip = (tooltip) =>
  [tooltip[0]].concat(tooltip.slice(1).reverse()).map((t, idx) => {
    if (typeof t === "string") {
      if (idx === 0) {
        return {name: t, idx: 0}
      } else {
        return {name: t, idx: tooltip.length - idx}
      }
    } else {
      return Object.assign({}, t, {idx: tooltip.length - idx})
    }
  })

const isLastBalanceZero = (lastBarVals = []) => {
  for (let i = 1; i < lastBarVals.length; i++) {
    if (lastBarVals[i] > 0) return false
  }
  return true
}

const removeLastElement = (list) => {
  list.splice(-1, 1)
  return list
}

const formatSnowball = (schedule) => common.prependMonths(schedule).map(([month, ...vs]) => [month].concat(vs.reverse()))

const generateTooltip = (debts) =>
  debts
    .concat()
    .reverse()
    .reduce(
      (memo, d) => {
        memo.push({
          name: d.name,
          type: "currency",
          color: d.color
        })
        return memo
      },
      ["Date"]
    )

export default ({schedule, debts, disabled, ariaLabel}) => (
  <common.Balances
    values={formatSnowball(schedule)}
    tooltip={generateTooltip(debts)}
    ariaLabel={ariaLabel}
    render={(props) => (
      <DoubleBar
        {...Object.assign({}, props, {
          isStacked: true,
          yAxis: "Debt",
          colors: colorsArr.slice(0, debts.length).reverse(),
          disabled
        })}
      />
    )}
  />
)
